import { axiosInstance } from "../utils";

const POLL_INTERVAL_MS = 2000; // 2 seconds between polls
const MAX_POLLS = 30; // ~60 seconds max polling time

// Stale-while-revalidate follow-through (PAN-413). A 200 that carries
// `stale: true, refreshing: true` means the backend served an old payload and queued a
// regen. The old card is already on screen, so this bound is deliberately tighter than
// the cold-start one above.
const STALE_POLL_INTERVAL_MS = 2000;
const MAX_STALE_POLLS = 10; // ~20 seconds

/**
 * Sleep helper for polling
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * AI Insights API service
 * Provides LLM-generated analysis for accounts including churn risk,
 * recommended actions, and expansion signals.
 */
export const aiInsightsService = {
  /**
   * Get AI insights for an account with automatic polling for async generation
   * @param {number|string} accountId - The account ID
   * @param {Object} options - Options
   * @param {string} options.template - Template name to use for analysis
   * @param {Function} options.onGenerating - Callback when status is 'generating'
   * @param {AbortSignal} options.signal - AbortController signal for cancellation
   * @returns {Promise<AIInsights>} The AI insights data
   */
  getInsights: async (accountId, options = {}) => {
    const { template, onGenerating, signal } = options;
    let polls = 0;

    // Build query params
    const params = {};
    if (template) {
      params.template = template;
    }

    const poll = async () => {
      // Check if aborted
      if (signal?.aborted) {
        throw new Error("Request cancelled");
      }

      const response = await axiosInstance.get(
        `/ai/accounts/${accountId}/insights/`,
        {
          params,
          signal,
          // Tell axios to not throw on 202
          validateStatus: (status) => status >= 200 && status < 300,
        },
      );

      // 202 = still generating, poll again
      if (response.status === 202) {
        polls++;

        if (polls >= MAX_POLLS) {
          throw new Error("Insights generation timed out. Please try again.");
        }

        // Notify caller that we're still generating
        if (onGenerating) {
          onGenerating(response.data);
        }

        await sleep(POLL_INTERVAL_MS);
        return poll();
      }

      // 200 = ready
      return response.data;
    };

    return poll();
  },

  /**
   * After a 200 that carried `stale: true, refreshing: true`, poll until the backend's
   * background refresh has landed. Resolves with the fresh payload, or null when the
   * bound is hit, the request is aborted, or a request fails — never throws, because
   * the caller already has a payload to show.
   * @param {number|string} accountId - The account ID
   * @param {Object} options - Options
   * @param {string} [options.template] - Template name to use for analysis
   * @param {AbortSignal} [options.signal] - AbortController signal for cancellation
   * @param {number} [options.intervalMs] - Poll interval; injectable so tests run with 0
   * @returns {Promise<AIInsights|null>} Fresh insights, or null if none arrived in time
   */
  pollUntilFresh: async (accountId, options = {}) => {
    const { template, signal, intervalMs = STALE_POLL_INTERVAL_MS } = options;
    const params = template ? { template } : {};

    for (let polls = 0; polls < MAX_STALE_POLLS; polls++) {
      await sleep(intervalMs);
      if (signal?.aborted) return null;

      let response;
      try {
        response = await axiosInstance.get(
          `/ai/accounts/${accountId}/insights/`,
          {
            params,
            signal,
            validateStatus: (status) => status >= 200 && status < 300,
          },
        );
      } catch (err) {
        return null;
      }

      // 202 = the cache was evicted mid-flight and a cold generation is running; keep waiting.
      if (response.status === 202) continue;
      // `||` not `??`: an empty 2xx body arrives as "" from axios and must read as
      // "nothing fresh", not as a payload.
      if (!response.data?.stale) return response.data || null;
    }

    return null;
  },

  /**
   * Refresh AI insights for an account (bypasses cache)
   * @param {number|string} accountId - The account ID
   * @param {Object} options - Options
   * @param {string} options.template - Template name to use for analysis
   * @param {Function} options.onGenerating - Callback when status is 'generating'
   * @param {AbortSignal} options.signal - AbortController signal for cancellation
   * @returns {Promise<AIInsights>} Fresh AI insights data
   */
  refreshInsights: async (accountId, options = {}) => {
    const { template, onGenerating, signal } = options;
    let polls = 0;

    // Build query params
    const params = {};
    if (template) {
      params.template = template;
    }

    // First, trigger the refresh
    const refreshResponse = await axiosInstance.post(
      `/ai/accounts/${accountId}/insights/refresh/`,
      {},
      {
        params,
        signal,
        validateStatus: (status) => status >= 200 && status < 300,
      },
    );

    // If refresh returns 202, poll for completion
    if (refreshResponse.status === 202) {
      if (onGenerating) {
        onGenerating(refreshResponse.data);
      }

      const poll = async () => {
        if (signal?.aborted) {
          throw new Error("Request cancelled");
        }

        await sleep(POLL_INTERVAL_MS);
        polls++;

        if (polls >= MAX_POLLS) {
          throw new Error("Insights generation timed out. Please try again.");
        }

        const response = await axiosInstance.get(
          `/ai/accounts/${accountId}/insights/`,
          {
            params,
            signal,
            validateStatus: (status) => status >= 200 && status < 300,
          },
        );

        if (response.status === 202) {
          if (onGenerating) {
            onGenerating(response.data);
          }
          return poll();
        }

        return response.data;
      };

      return poll();
    }

    return refreshResponse.data;
  },

  /**
   * Get available AI insight templates
   * @returns {Promise<TemplatesResponse>} Available templates and default
   */
  getTemplates: async () => {
    const response = await axiosInstance.get("/ai/templates/");
    return response.data;
  },
};

/**
 * @typedef {Object} RecommendedAction
 * @property {number} priority - Priority order (1 = highest)
 * @property {string} action - The recommended action
 * @property {string} rationale - Why this action is recommended
 */

/**
 * @typedef {Object} AIInsights
 * @property {string} summary - LLM-generated summary of the account
 * @property {'high'|'medium'|'low'} churn_risk - Churn risk level
 * @property {string[]} churn_risk_factors - Factors contributing to churn risk
 * @property {string[]} supporting_evidence - Evidence supporting the analysis
 * @property {RecommendedAction[]} recommended_actions - Prioritized actions
 * @property {string[]} expansion_signals - Signals indicating expansion opportunity
 * @property {'high'|'medium'|'low'} confidence - Confidence level of the analysis
 * @property {string} generated_at - ISO datetime when insights were generated
 * @property {string} valid_until - ISO datetime when insights expire
 * @property {number} cache_ttl_seconds - Cache TTL in seconds
 * @property {boolean} cached - Whether this response is from cache
 * @property {boolean} [stale] - Served payload is past freshness or soft-invalidated
 * @property {boolean} [refreshing] - A background regeneration was queued for it
 * @property {boolean} can_refresh - Whether manual refresh is available
 * @property {number|null} refresh_cooldown_remaining - Seconds until refresh available
 * @property {string} [refresh_error] - Error message if refresh failed
 */

/**
 * @typedef {Object} Template
 * @property {string} name - Template identifier (e.g., 'account_health')
 * @property {string} display_name - Human-readable name
 * @property {string} description - Description of what the template analyzes
 */

/**
 * @typedef {Object} TemplatesResponse
 * @property {Template[]} templates - Available templates
 * @property {string} default_template - Name of the default template
 */
